import { Grid } from '@mui/material';
import { Coordinate, Location } from '@nx-smartmonkey/shared/domain';
import { RoutalLoader, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';
import { parseAddresses } from '../../../../helpers/excel/Parsers';
import { useRetrieveActiveProject } from '../../../../hooks/project/useRetrieveActiveProject';
import Text from '../../../../react-components/Text';
import { INTERCOM_EVENTS, intercom } from '../../../../services/intercom';
import { CreateClientsList } from './fields/CreateClientsList';

interface GeocodeAddressesProps {
  createClientsList: CreateClientsList;
  onComplete: (createClientsList: CreateClientsList) => void;
}

export const GeocodeAddresses = ({ createClientsList, onComplete }: GeocodeAddressesProps) => {
  const { data: activeProject } = useRetrieveActiveProject();
  const [isGeocoding, setIsGeocoding] = useState<boolean>(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    // Geocode client directions here
    const geocodeAddresses = async () => {
      setIsGeocoding(true);
      try {
        const clientLocations = (
          (await parseAddresses({
            addresses: createClientsList.getClientsAddresses(),
            projectId: activeProject!.id,
          })) as (Location | null)[]
        ).map((clientAddress) =>
          clientAddress?.lat &&
          clientAddress?.lng &&
          Coordinate.areCoordinatesValid(clientAddress.lat, clientAddress.lng)
            ? clientAddress
            : null
        );

        const newCreateClientsList = createClientsList.setClientAddresses(clientLocations);
        onComplete(newCreateClientsList);
      } catch (error) {
        setError(error as string);
        intercom.track(INTERCOM_EVENTS.plan.geocode_credit_finished);
      } finally {
        setIsGeocoding(false);
      }
    };

    geocodeAddresses();
  }, [activeProject, onComplete, createClientsList]);

  if (isGeocoding) {
    return (
      <Grid container style={{ width: `100%` }} alignItems={`center`} justifyContent={`center`}>
        <Grid container alignItems={`center`} flexDirection={`column`}>
          <RoutalLoader />
          <VSpacer small />
          <Text variant="label" intlId={`plans.dialogs.geocode_addresses.loading.text`} />
        </Grid>
      </Grid>
    );
  }

  if (!error) return null;

  if (error === `highway.maps.error.not_enough_credits`) {
    return (
      <Grid container alignItems={`center`} flexDirection={`column`}>
        <Text variant="h5" intlId={`plans.dialogs.geocode_addresses.geocode_credits.title`} />
        <VSpacer small />
        <Text intlId={`plans.dialogs.geocode_addresses.geocode_credits.text`} />
        <VSpacer small />
      </Grid>
    );
  }

  if (error !== `highway.maps.error.not_enough_credits`) {
    return (
      <Grid container alignItems={`center`} flexDirection={`column`}>
        <Text variant="h5" intlId={`plans.dialogs.geocode_addresses.unknown_error.title`} />
        <VSpacer small />
        <Text intlId={`plans.dialogs.geocode_addresses.unknown_error.text`} />
        <VSpacer small />
        <Text variant="label">{error}</Text>
      </Grid>
    );
  }

  return null;
};
